import {
    BronsonSelector,
    BronsonSelectorProps,
    BronsonSpinner,
    BronsonSpinnerProps,
    filters,
} from '@dh/bronson-react';
import React, { useContext, useEffect, useState } from 'react';

import { AppService } from '../../App.service';
import { SelectorItems, Totals } from '../../components';
import {
    ItemSelectorProps,
    SelectorProps,
} from '../../components/Selector/selector.type';
import { initTotals } from '../../components/Totals/totals.constants';
import { TotalPropsComponent } from '../../components/Totals/totals.type';
import { ProvidersContext } from '../../contexts/providers.context';
import {
    FinancialProduct,
    ProductDurationsProps,
    ProductPricingParams,
    SimulationData,
    StorefrontModel,
    VehicleModel,
} from '../../utils/interfaces';
import TrackingManager from '../../utils/tracking-manager.class';
import {
    buildIAnItemSelectorArray,
    getIdFromVehicleModels,
    getPropValueFromVehicleModelsTrimLineDetails,
} from '../../utils/util.class';
import { FCalculatorError } from '../CalculatorError/error';
import { SelectorErrors } from '../CalculatorError/error.types';
import {
    durationsSelector,
    percentageSelector,
    SelectorTypes,
    TitleMethods,
} from './calculator.constants';
import { calculatorLabels } from './calculator.labels';

/**
 * This is the view for the financial calculator component.
 * @returns a React Function Component.
 */
export const FinancialCalculator: React.FC<{
    financialProducts: Array<FinancialProduct>;
    vehicleModels: Array<VehicleModel>;
    simulation: SimulationData;
    externalStoreFront?: StorefrontModel;
}> = ({ financialProducts, vehicleModels, simulation, externalStoreFront }) => {
    const oneHundred = 100;
    const config = useContext(ProvidersContext);
    const isPFA = config.isPFA === false ? config.isPFA : true;
    const isAudiUsed = !!config.price && config.brand.toLowerCase() === 'audi';
    const trackingManager = new TrackingManager(config);
    const appService = new AppService();
    const [simulationData] = useState(simulation);
    const [serviceError, setServiceError] = useState<{
        error: boolean;
        message: string | SelectorErrors;
    }>({
        error: false,
        message: '',
    });

    const [bronsonSpinnerProps, setBronsonSpinnerProps] =
        useState<BronsonSpinnerProps>({
            showSpinner: false,
            spinnerWrapperClassModifier: 'c-spinner--full-page',
        });

    let productDurationSelected: ItemSelectorProps;
    let percentagePaymentSelected: number;

    const [totalData, setTotalData] = useState<TotalPropsComponent>({
        ...initTotals,
        trackingManager,
        model: config.model,
        version: config.version,
        year: config.year,
        brand: config.brand,
        errorOcurred: (event) => setServiceError({ error: event, message: '' }),
        hideDefaultLegalTerms: isAudiUsed,
        isUsed: isAudiUsed,
        externalStoreFront,
    });

    const modelId = getIdFromVehicleModels(vehicleModels, config);

    useEffect(() => {
        productDurationSelected = buildIAnItemSelectorArray(
            financialProducts[0].productDurations,
        )[0];

        const totalHitch = getTheTotalHitch(
            financialProducts[0].productDurations,
        );
        const downPaymentPercentage =
            financialProducts[0].productDurations[0].minimumDownPayment;
        const months = productDurationSelected.valueNumber;
        const productName = financialProducts[0].name;
        const paymentMonthly = simulationData.installmentAmount;
        const retailPrice = getRetailPrice();

        setTotalData({
            ...totalData,
            modelId: modelId || 0,
            duration: months,
            durationId: productDurationSelected.id,
            productId: financialProducts[0].id,
            productName,
            downPaymentPercentage,
            legalTerms: financialProducts[0].legalTerms,
            installmentAmount: paymentMonthly,
            versionId: vehicleModelTrimLineId || 0,
            retailPrice,
            totalHitch,
        });

        trackingManager.loadTheFinancialCalculator({
            productName,
            downpaymentValue: totalHitch,
            downpaymentPercentage: downPaymentPercentage * oneHundred,
            paymentMonthly,
            financingDuration: months,
            cashPrice: retailPrice,
            productType: financialProducts[0].financialProductTypeId.toString(),
        });
    }, []);

    /**
     * This function return the retail price or
     * 0 if the vehicle is not found.
     * @returns retailPrice.
     */
    const getRetailPrice = (): number => {
        return isAudiUsed
            ? Number(config.price)
            : getPropValueFromVehicleModelsTrimLineDetails(
                  vehicleModels,
                  config,
                  'retailPrice',
              ) || 0;
    };

    /**
     * This method change the state of the error flag
     * and set the message error to the alarm service.
     * @param error string.
     */
    const setError = (error: string | SelectorErrors): void => {
        setServiceError({
            error: true,
            message: error,
        });
    };

    /**
     * This function make the calculation of the total hitch.
     * @param productDuration Array<ProductDurationsProps>.
     * @param percentage number.
     * @returns Total Hitch.
     */
    const getTheTotalHitch = (
        productDuration: Array<ProductDurationsProps>,
        percentage?: number,
    ): number => {
        try {
            const retailPrice = getRetailPrice();
            let percentageValue: number;

            if (percentage) {
                percentageValue = percentage || 0;
                return retailPrice * percentageValue;
            }

            const productDurationsItems =
                buildIAnItemSelectorArray(productDuration);
            const percentagePayments = productDurationPaymentsToArray(
                productDurationsItems[0],
            );

            percentageValue = percentagePayments[0].id || 0;
            return retailPrice * percentageValue;
        } catch (error) {
            setError({
                error: error.message,
                method: TitleMethods.getTheTotalHitch,
            });
            return 0;
        }
    };

    const [vehicleModelTrimLineId] = useState<number | undefined>(
        getPropValueFromVehicleModelsTrimLineDetails(
            vehicleModels,
            config,
            'id',
            isAudiUsed,
        ),
    );

    /**
     * This state is used to render the financial-products selector component,
     * on his method property it component will make the followings:
     *
     *  - It will call the fetchSimulationService to fetch the new installmentAmount value.
     *  - It will set the new items to the product-durations selector component.
     *  - It will set the new items to the percentage selector component.
     *
     * Its items returned will be filter to prevent render the type `Leasing`
     * because this type is not allowed.
     */
    const [financialData] = useState<BronsonSelectorProps>({
        items: financialProducts.map((product) => {
            return {
                id: product.id.toString(),
                description: product.description,
                title: product.name,
                subtitle: product.summary,
            };
        }),
        method: (item) => {
            try {
                const productSelected = financialProducts.find(
                    (product) => product.id === Number(item),
                );

                if (productSelected) {
                    const productDurationsSelected =
                        productSelected.productDurations;

                    const newProductDurations = buildIAnItemSelectorArray(
                        productDurationsSelected,
                    );

                    const newPercentagePayment = productDurationPaymentsToArray(
                        newProductDurations[0],
                    );

                    productDurationSelected = newProductDurations[0];

                    fetchSimulationService({
                        financialProductDurationId: productDurationSelected.id,
                        vehicleModelTrimLineId: vehicleModelTrimLineId || 0,
                        downPaymentPercentage:
                            productDurationSelected.minimumDownPayment || 0,
                        ...(isAudiUsed
                            ? {
                                  retailPrice: config.price,
                                  modelYear: config.year,
                              }
                            : {}),
                    }).then((paymentMonthly) => {
                        const totalHitch = getTheTotalHitch(
                            productSelected.productDurations,
                        );
                        const downPaymentPercentage =
                            newPercentagePayment[0].valueNumber;
                        const months = productDurationSelected.valueNumber;

                        setTotalData((previous) => {
                            return {
                                ...previous,
                                productName: productSelected.name,
                                productId: productSelected.id,
                                duration: months,
                                durationId: productDurationSelected.id,
                                versionId: vehicleModelTrimLineId || 0,
                                legalTerms: productSelected?.legalTerms || '',
                                downPaymentPercentage,
                                totalHitch,
                            };
                        });

                        trackingManager.changeFinancialProducts(
                            {
                                ...trackingManager.getFinancialProductData(),
                                productName: productSelected.name,
                                downpaymentValue: totalHitch,
                                downpaymentPercentage:
                                    downPaymentPercentage * oneHundred,
                                paymentMonthly,
                                financingDuration: months,
                                productType:
                                    productSelected.financialProductTypeId.toString(),
                            },
                            productSelected.id.toString(),
                        );
                    });

                    setProductDurations((prev) => ({
                        ...prev,
                        items: newProductDurations,
                    }));

                    setPercentagePayment((prev) => ({
                        ...prev,
                        items: newPercentagePayment,
                    }));
                }
            } catch (error) {
                setError({
                    error: error.message,
                    selector: SelectorTypes.financialProducts,
                });
            }
        },
    });

    /**
     * This function fetch the simulation operation and set
     * the new installmentAmount value into total data object.
     * @param request ProductPricingParams
     */
    const fetchSimulationService = async (
        request: ProductPricingParams,
    ): Promise<number> => {
        setBronsonSpinnerProps({
            ...bronsonSpinnerProps,
            showSpinner: true,
        });
        const result = await appService
            .fetchSimulation(config.brand, isPFA, request)
            .then((response) => {
                const simulationResponse = response.data;
                const paymentAMonth = simulationResponse.installmentAmount;

                setTotalData((prev) => {
                    return {
                        ...prev,
                        installmentAmount: paymentAMonth,
                        retailPrice: getRetailPrice(),
                    };
                });

                return paymentAMonth;
            })
            .catch(() => setError(''));
        setBronsonSpinnerProps({
            ...bronsonSpinnerProps,
            showSpinner: false,
        });
        return result;
    };

    /**
     * This function return an ItemSelectorProps array to
     * payments from a product duration selected.
     * @param productDuration ItemSelectorProps.
     * @returns ItemSelectorProps[].
     */
    const productDurationPaymentsToArray = (
        productDuration: ItemSelectorProps,
    ): ItemSelectorProps[] => {
        try {
            const minimum = productDuration.minimumDownPayment;
            const maximum = productDuration.maximumDownPayment;

            if (minimum && maximum) {
                let val = minimum;
                const percentagePayments: Array<ItemSelectorProps> = [];

                do {
                    const valueFixed = Number(val).toFixed(2);
                    percentagePayments.push({
                        id: Number(valueFixed),
                        value: `${valueFixed.split('.')[1]}%`,
                        valueNumber: Number(valueFixed),
                    });
                } while (Number(Number((val += 0.05)).toFixed(2)) <= maximum);
                return percentagePayments;
            }

            return [];
        } catch (error) {
            setError({
                error: error.message,
                method: TitleMethods.productDurationPaymentsToArray,
            });
            return [];
        }
    };

    /**
     * This state is used to render the product-durations selector component,
     * on his method property it component will make the followings:
     *
     *  - It will call the fetchSimulationService to fetch the new installmentAmount value.
     *  - It will set the new items to the percentage selector component.
     */
    const [productDurations, setProductDurations] = useState<SelectorProps>({
        method: ({ items, itemId }) => {
            try {
                const productDuration = items.filter(
                    (duration) => duration.id === itemId,
                )[0];

                productDurationSelected = productDuration;

                fetchSimulationService({
                    financialProductDurationId: productDuration.id,
                    vehicleModelTrimLineId: vehicleModelTrimLineId || 0,
                    downPaymentPercentage: percentagePaymentSelected
                        ? percentagePaymentSelected
                        : productDuration.minimumDownPayment || 0,
                    ...(isAudiUsed
                        ? { retailPrice: config.price, modelYear: config.year }
                        : {}),
                }).then((paymentMonthly) => {
                    const months = productDurationSelected.valueNumber;

                    setTotalData((prev) => ({
                        ...prev,
                        duration: months,
                        durationId: productDuration.id,
                    }));

                    trackingManager.changeMonthDuration(
                        paymentMonthly,
                        months,
                        itemId.toString(),
                    );
                });
            } catch (error) {
                setError({
                    error: error.message,
                    selector: SelectorTypes.productDurations,
                });
            }
        },
        items: buildIAnItemSelectorArray(financialProducts[0].productDurations),
        id: durationsSelector,
    });

    /**
     * This state is used to render the percentage selector component,
     * on his method property it component will call the fetchSimulationService
     * to fetch the new installmentAmount value.
     */
    const [percentagePayment, setPercentagePayment] = useState<SelectorProps>({
        method: ({ itemId }) => {
            try {
                percentagePaymentSelected = itemId;

                fetchSimulationService({
                    financialProductDurationId: productDurationSelected.id,
                    vehicleModelTrimLineId: vehicleModelTrimLineId || 0,
                    downPaymentPercentage: percentagePaymentSelected,
                    ...(isAudiUsed
                        ? { retailPrice: config.price, modelYear: config.year }
                        : {}),
                }).then((paymentMonthly) => {
                    const totalHitch = getTheTotalHitch(
                        [],
                        percentagePaymentSelected,
                    );

                    setTotalData((previous) => {
                        return {
                            ...previous,
                            downPaymentPercentage: percentagePaymentSelected,
                            totalHitch,
                        };
                    });

                    trackingManager.changeDownPaymentPercentage(
                        totalHitch,
                        `${percentagePaymentSelected * oneHundred}%`,
                        paymentMonthly,
                    );
                });
            } catch (error) {
                setError({
                    error: error.message,
                    selector: SelectorTypes.downPaymentPercentage,
                });
            }
        },
        items: productDurationPaymentsToArray(productDurations.items[0]),
        id: percentageSelector,
    });

    if (serviceError.error) {
        return (
            <FCalculatorError config={config} error={serviceError.message} />
        );
    }

    const brand = filters.capitalize(config.brand);

    return (
        <div className='content-calculator'>
            <div className='c-compare-and-select-container'>
                <h5>
                    <b>{calculatorLabels.title}</b>
                </h5>

                <p>{calculatorLabels.summary}</p>
                <p>{calculatorLabels.description.replace('{brand}', brand)}</p>
            </div>

            <div className='c-compare-and-select-container u-mt-xlarge'>
                <h5>
                    <b>{calculatorLabels.financingTitle}</b>
                </h5>
            </div>

            <BronsonSelector {...financialData} />

            <div className='c-compare-and-select-container u-mt-xlarge u-pb-xsmall'>
                <h5>
                    <b>{calculatorLabels.percentageTitle}</b>
                </h5>
            </div>

            <SelectorItems {...percentagePayment} />

            <div className='c-compare-and-select-container u-mt-large u-pb-xsmall'>
                <h5>
                    <b>{calculatorLabels.timeToPaymentTitle}</b>
                </h5>
            </div>

            <SelectorItems {...productDurations} />
            <Totals {...totalData} />
            <BronsonSpinner config={bronsonSpinnerProps} />
        </div>
    );
};
